import { Injectable, computed, signal } from '@angular/core';

const CONSENT_KEY = 'wwhxp-consent';
const CONSENT_VERSION = 1;

interface ConsentRecord {
  analytics: boolean;
  v: number;
  ts: string;
}

/**
 * Verwaltet die Cookie-/Tracking-Einwilligung des Nutzers (TTDSG/DSGVO).
 * Technisch notwendige Speicher (Login, Sprache) sind nicht betroffen –
 * dieser Service steuert ausschließlich die einwilligungspflichtige Analyse.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly state = signal<ConsentRecord | null>(this.load());

  /** Ob der Nutzer bereits eine ausdrückliche Entscheidung getroffen hat. */
  readonly hasDecision = computed(() => this.state() !== null);

  /** Ob die anonyme Reichweitenmessung/Analyse erlaubt ist. */
  readonly analyticsAllowed = computed(() => this.state()?.analytics === true);

  /** Einwilligung in die anonyme Analyse erteilen. */
  accept(): void {
    this.persist({ analytics: true, v: CONSENT_VERSION, ts: new Date().toISOString() });
  }

  /** Nur technisch notwendige Speicher zulassen (Analyse ablehnen). */
  reject(): void {
    this.persist({ analytics: false, v: CONSENT_VERSION, ts: new Date().toISOString() });
  }

  private persist(record: ConsentRecord): void {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    } catch {
      // Speicher ggf. nicht verfügbar (privater Modus) – Entscheidung bleibt im Speicher.
    }
    this.state.set(record);
  }

  private load(): ConsentRecord | null {
    try {
      const raw = localStorage.getItem(CONSENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ConsentRecord;
      if (parsed && parsed.v === CONSENT_VERSION && typeof parsed.analytics === 'boolean') {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }
}
